"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { LottieSvg, type LottieHandle } from "lottie-react";
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from "framer-motion";
import { STAY_BANNER, listingService } from "../services";
import { useStayBannerAnimationQuery } from "../api/queries";

/** Docks once the page has scrolled this far (the app's `_guestFavoriteStayDockOffset`). */
const DOCK_SCROLL_OFFSET = 2;
/** `listingGuestFavoriteStayShadow`, `#2E000000`. */
const SHADOW_COLOR = "rgba(0, 0, 0, 0.18)";

/**
 * The app's Guest Favourite stay banner (`ListingGuestFavoriteStayBanner`), sitting on the
 * booking bar: the "Guest Favorite Stay! This place is usually booked" pill rises out from
 * under the bar and forms (frames 0–60), and once the page scrolls it docks into a grey
 * strip across the bar's top (60–90). Scrolled before the intro ends, the intro hurries to
 * its end in 0.2s first. The pill's border and shadow are drawn here, as the app's
 * delegates and `BoxShadow` do.
 */
const GuestFavouriteStayBanner: React.FC = () => {
  const { data } = useStayBannerAnimationQuery(true);
  const animation = useMemo(
    () => (data ? listingService.stayBannerWithPill(data) : null),
    [data],
  );
  const reducedMotion = useReducedMotion();

  // The canvas scales to the bar's width.
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const node = box.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const scale = width / STAY_BANNER.canvas.width;

  // The Lottie's absolute frame drives the shadow and the rise, without re-rendering.
  const frame = useMotionValue(0);
  const rise = useTransform(
    frame,
    (f) => listingService.stayBannerRiseOffset(f) * scale,
  );
  const shadowOpacity = useTransform(frame, (f) =>
    listingService.stayBannerShadowOpacity(f),
  );

  const player = useRef<LottieHandle>(null);
  const stage = useRef({ introDone: false, dockWanted: false, docking: false });

  const absoluteFrame = () => {
    const item = player.current?.animationItem;
    return item ? item.firstFrame + item.currentFrame : 0;
  };

  const dock = () => {
    const current = player.current;
    const s = stage.current;
    if (!current || !s.dockWanted || s.docking) return;
    if (!s.introDone) {
      // Hurry the rest of the intro, then dock from its end.
      const remaining = STAY_BANNER.introEnd - absoluteFrame();
      current.setSpeed(
        Math.max(
          1,
          remaining / (STAY_BANNER.fps * STAY_BANNER.fastForwardSeconds),
        ),
      );
      return;
    }
    s.docking = true;
    current.setSpeed(1);
    current.playSegments([STAY_BANNER.dockStart, STAY_BANNER.dockEnd]);
  };

  const handleLoaded = () => {
    const current = player.current;
    if (!current) return;
    if (reducedMotion) {
      stage.current = { introDone: true, dockWanted: true, docking: true };
      current.seek(STAY_BANNER.dockEnd - 1);
      current.pause();
      frame.set(STAY_BANNER.dockEnd);
      return;
    }
    current.playSegments([0, STAY_BANNER.introEnd]);
  };

  const handleComplete = () => {
    if (stage.current.docking) return;
    stage.current.introDone = true;
    dock();
  };

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY <= DOCK_SCROLL_OFFSET || stage.current.dockWanted)
        return;
      stage.current.dockWanted = true;
      dock();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const { pill, shadow, canvas, dockedStripBottom } = STAY_BANNER;

  return (
    <div
      ref={box}
      aria-hidden
      className="pointer-events-none absolute inset-x-0 bottom-full overflow-hidden"
      style={{ height: dockedStripBottom * scale }}
    >
      {animation && scale > 0 && (
        <motion.div
          className="absolute inset-x-0 top-0"
          style={{ height: canvas.height * scale, y: rise }}
        >
          <motion.div
            className="absolute"
            style={{
              left: pill.left * scale,
              top: pill.top * scale,
              width: pill.width * scale,
              height: pill.height * scale,
              borderRadius: pill.radius * scale,
              boxShadow: `0 ${shadow.offsetY * scale}px ${shadow.blur * scale}px ${shadow.spread * scale}px ${SHADOW_COLOR}`,
              opacity: shadowOpacity,
            }}
          />
          <LottieSvg
            lottieRef={player}
            src={animation}
            loop={false}
            autoplay={false}
            rendererSettings={{ preserveAspectRatio: "none" }}
            subscriptions={{
              ready: handleLoaded,
              frame: () => frame.set(absoluteFrame()),
              complete: handleComplete,
            }}
            className="absolute inset-0"
          />
        </motion.div>
      )}
    </div>
  );
};

export default GuestFavouriteStayBanner;
